import React, { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm, Select, Switch, Table } from 'antd';

import type { ColumnsType } from 'antd/es/table';
import type { SnsPlatform } from '../interface/I_AccountsManagement';

type PlatformType = 'FACEBOOK' | 'INSTAGRAM' | 'YOUTUBE';

const platformOptions = [
  { label: 'Facebook', value: 'FACEBOOK' },
  { label: 'Instagram', value: 'INSTAGRAM' },
  { label: 'YouTube', value: 'YOUTUBE' },
];

interface SNSConnectionTableProps {
  data: SnsPlatform[];
  loading?: boolean;
  onAdd: (record: Partial<SnsPlatform>) => void;
  onEdit: (id: number, record: Partial<SnsPlatform>) => void;
  onDelete: (id: number) => void;
}

const SNSConnectionTable: React.FC<SNSConnectionTableProps> = ({
  data,
  loading,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editRecord, setEditRecord] = useState<Partial<SnsPlatform>>({});
  const [adding, setAdding] = useState(false);
  const [addRecord, setAddRecord] = useState<Partial<SnsPlatform>>({
    platformType: 'FACEBOOK',
    crawlActive: true,
  });

  const isEditing = (idx: number) => editingId === idx;

  const columns: ColumnsType<SnsPlatform & { key: number }> = [
    {
      title: 'Platform',
      dataIndex: 'platformType',
      render: (text, record, idx) =>
        isEditing(idx) ? (
          <Select
            value={editRecord.platformType as PlatformType}
            options={platformOptions}
            onChange={(v) => setEditRecord({ ...editRecord, platformType: v })}
            style={{ width: 120 }}
          />
        ) : (
          platformOptions.find((opt) => opt.value === record.platformType)?.label
        ),
    },
    {
      title: 'Handle/URL',
      dataIndex: 'handleOrUrl',
      render: (text, record, idx) =>
        isEditing(idx) ? (
          <Input
            value={editRecord.handleOrUrl}
            onChange={(e) => setEditRecord({ ...editRecord, handleOrUrl: e.target.value })}
            style={{ minWidth: 200 }}
          />
        ) : (
          <a href={record.handleOrUrl} target="_blank" rel="noopener noreferrer">
            {record.handleOrUrl}
          </a>
        ),
    },
    {
      title: 'Enable Crawler',
      dataIndex: 'crawlActive',
      render: (text, record, idx) =>
        isEditing(idx) ? (
          <Switch
            checked={!!editRecord.crawlActive}
            onChange={(v) => setEditRecord({ ...editRecord, crawlActive: v })}
          />
        ) : record.crawlActive ? (
          'Yes'
        ) : (
          'No'
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'action',
      render: (_, record, idx) => {
        if (isEditing(idx)) {
          return (
            <>
              <Button
                type="link"
                onClick={() => {
                  onEdit(idx, editRecord);
                  setEditingId(null);
                  setEditRecord({});
                }}
                style={{ marginRight: 8 }}
              >
                Save
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setEditingId(null);
                  setEditRecord({});
                }}
              >
                Cancel
              </Button>
            </>
          );
        }
        return (
          <>
            <Button
              type="link"
              onClick={() => {
                setEditingId(idx);
                setEditRecord(record);
              }}
            >
              Edit
            </Button>
            <Popconfirm title="Are you sure to delete?" onConfirm={() => onDelete(idx)}>
              <Button type="link" danger>
                Delete
              </Button>
            </Popconfirm>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Table
        rowKey={(_, idx) => (idx !== undefined ? idx.toString() : Math.random().toString())}
        columns={columns}
        dataSource={data.map((item, idx) => ({ ...item, key: idx }))}
        loading={loading}
        pagination={false}
        size="small"
        style={{ marginBottom: 16 }}
      />
      {adding ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Select
            value={addRecord.platformType as PlatformType}
            options={platformOptions}
            onChange={(v) => setAddRecord({ ...addRecord, platformType: v })}
            style={{ width: 120 }}
          />
          <Input
            value={addRecord.handleOrUrl}
            onChange={(e) => setAddRecord({ ...addRecord, handleOrUrl: e.target.value })}
            placeholder="Handle/URL"
          />
          <Switch
            checked={!!addRecord.crawlActive}
            onChange={(v) => setAddRecord({ ...addRecord, crawlActive: v })}
          />
          <Button
            type="primary"
            onClick={() => {
              onAdd(addRecord);
              setAddRecord({ platformType: 'FACEBOOK', crawlActive: true });
              setAdding(false);
            }}
            disabled={!addRecord.handleOrUrl}
          >
            Add
          </Button>
          <Button
            onClick={() => {
              setAdding(false);
              setAddRecord({ platformType: 'FACEBOOK', crawlActive: true });
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button icon={<PlusOutlined />} onClick={() => setAdding(true)} type="dashed" block>
          Add SNS Connection
        </Button>
      )}
    </>
  );
};

export default SNSConnectionTable;
